// src/pages/BookLandingPage.js

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { db } from '../config/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

const BookLandingPage = () => {
  const { bookId } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookData = async () => {
      try {
        const docRef = doc(db, 'bookPages', bookId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setBook({ id: docSnap.id, ...docSnap.data() });
        } else {
          setBook(null); 
        }
      } catch (error) {
        console.error("Error fetching book: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookData();
  }, [bookId]);

  // CSS for the premium background pattern and classy heading
  const premiumBackgroundStyle = `
    .bg-premium-pattern { 
      background-color: #f2f7fa; 
      background-image: radial-gradient(#cfdbe6 1.5px, transparent 1.5px); 
      background-size: 28px 28px; 
    }
    .classy-heading { 
      font-family: 'Playfair Display', serif; 
    }
  `;

  if (loading) return (
    <>
      <style>{premiumBackgroundStyle}</style>
      <div className="min-vh-100 d-flex justify-content-center align-items-center bg-premium-pattern">
        <Spinner animation="grow" variant="primary" />
      </div>
    </>
  );

  if (!book) return (
    <>
      <style>{premiumBackgroundStyle}</style>
      <div className="min-vh-100 d-flex justify-content-center align-items-center bg-premium-pattern">
        <Container className="text-center py-5 bg-white shadow-lg" style={{ maxWidth: '600px', borderRadius: '2rem' }}>
          <h2 className="fw-bold">Book Not Found</h2>
          <p className="text-muted">The page you are looking for does not exist or has been removed.</p>
          <Link to="/" className="btn btn-primary px-4 rounded-pill mt-3">Back to Home</Link>
        </Container>
      </div>
    </>
  );

  const bonusMaterials = [
    { url: book.bonus1Url, title: "Glossary & Resources" },
    { url: book.bonus2Url, title: "Coloring Pages" },
    { url: book.bonus3Url, title: "Worksheets" }
  ].filter(b => b.url);

  return (
    <div className="bg-premium-pattern d-flex flex-column" style={{ minHeight: '100vh' }}>
      <Helmet>
        <title>{book.title} | Little Hibba</title>
      </Helmet>

      <style>{premiumBackgroundStyle}</style>

      {/* İÇERİK KONTEYNERİ */}
      <Container className="p-0 flex-grow-1" style={{ maxWidth: '1200px' }}>
        
        {/* ==========================================
            BÖLÜM 1: BAŞLIK VE GENİŞ FLIPBOOK (IFRAME)
            ========================================== */}
        {/* Header'ın altında kalmaması için paddingTop: 120px eklendi */}
        <div className="px-4 px-lg-5 text-center" style={{ paddingTop: '120px', paddingBottom: '2rem' }}>
          
          <div className="mb-3">
            <span className="badge px-4 py-2 rounded-pill fw-bold text-uppercase" style={{ backgroundColor: '#d8efff', color: '#0056b3', letterSpacing: '1px' }}>
              LITTLE HIBBA LIBRARY
            </span>
          </div>
          
          <h1 className="fw-bolder mb-3 text-dark" style={{ fontSize: '3.5rem', letterSpacing: '-0.02em', color: '#222' }}>
            {book.title}
          </h1>
          
          <p className="text-secondary mx-auto mb-5" style={{ fontSize: '1.2rem', lineHeight: '1.8', maxWidth: '850px' }}>
            {book.description}
          </p>
          
          {/* GENİŞ IFRAME FLIPBOOK ALANI */}
          {(book.flipbookEmbedUrl || book.coverUrl) && (
            <div className="mx-auto w-100 mb-4" style={{ maxWidth: '1000px' }}>
              {book.flipbookEmbedUrl ? (
                <div className="rounded-4 overflow-hidden shadow-lg border border-light" style={{ width: '100%', height: '650px', backgroundColor: '#fdfdfd' }}>
                  <iframe 
                    src={book.flipbookEmbedUrl} 
                    width="100%" 
                    height="100%" 
                    frameBorder="0" 
                    webkitallowfullscreen="true" 
                    mozallowfullscreen="true" 
                    allowFullScreen
                    title={`${book.title} Flipbook`}
                  ></iframe>
                </div>
              ) : (
                <img src={book.coverUrl} alt={`${book.title} Cover`} className="img-fluid rounded-4 shadow-lg border border-light" style={{ maxHeight: '600px' }} />
              )}
            </div>
          )}
        </div>

        {/* ==========================================
            BÖLÜM 2: SES OYNATICI
            ========================================== */}
        {book.audioUrl && (
          <div className="px-4 px-lg-5 py-4">
            <div className="p-4 rounded-4 shadow-sm border border-light border-start border-5 border-warning mx-auto position-relative overflow-hidden bg-white" style={{ maxWidth: '800px' }}>
              <div className="position-absolute top-0 end-0 bottom-0 w-50" style={{ background: 'linear-gradient(90deg, transparent, #ffffff)', zIndex: 0 }}></div>
              <div className="position-relative" style={{ zIndex: 1 }}>
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <div className="bg-warning bg-opacity-10 text-warning rounded-circle d-flex justify-content-center align-items-center shadow-sm" style={{ width: '50px', height: '50px' }}>
                    <i className="bi bi-headphones fs-4"></i>
                  </div>
                  <h4 className="mb-0 ms-3 fw-bold text-dark">Listen to the Story</h4>
                </div>
                <audio controls className="w-100 shadow-sm rounded-pill mt-2" style={{ height: '50px', outline: 'none', backgroundColor: '#f8f9fa' }}>
                  <source src={book.audioUrl} type="audio/mpeg" />
                </audio>
              </div>
            </div>
          </div>
        )}

        {/* ==========================================
            BÖLÜM 3: BONUS MATERYALLER (3 SÜTUN)
            ========================================== */}
        {bonusMaterials.length > 0 && (
          <div className="px-4 px-lg-5 py-5">
            <h3 className="fw-bold text-center mb-5 text-dark" style={{ fontSize: '2.2rem' }}>Bonus Materials</h3>
            <Row className="g-4 justify-content-center mx-auto" style={{ maxWidth: '1000px' }}>
              {bonusMaterials.map((bonus, index) => (
                <Col md={4} key={index}>
                  <Card className="h-100 border-0 rounded-4 shadow-sm bg-white hover-shadow transition-all text-center p-4">
                    <Card.Body className="d-flex flex-column align-items-center justify-content-center p-0">
                      <div className="bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center mb-4 shadow-sm" style={{ width: '70px', height: '70px', fontSize: '2.5rem' }}>
                        <i className="bi bi-cloud-arrow-down-fill"></i>
                      </div>
                      <Card.Title className="fw-bold mb-4 fs-5 text-dark">{bonus.title}</Card.Title>
                      <Button href={bonus.url} target="_blank" variant="outline-success" className="px-4 py-2 rounded-pill fw-bold mt-auto w-100 border-2">
                        Download
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* ==========================================
            BÖLÜM 4: VİDEO (TAM GENİŞLİK)
            ========================================== */}
        {book.videoUrl && (
          <div className="px-4 px-lg-5 py-5 mb-5">
            <h3 className="fw-bold text-center mb-5 text-dark" style={{ fontSize: '2.2rem' }}>Watch the Video</h3>
            <div className="mx-auto" style={{ maxWidth: '900px' }}>
              <div className="ratio ratio-16x9 rounded-4 overflow-hidden shadow-lg border border-light">
                <iframe src={book.videoUrl.replace("watch?v=", "embed/")} title={`${book.title} Video`} allowFullScreen style={{ border: 'none' }}></iframe>
              </div>
            </div>
          </div>
        )}
      </Container>

      {/* ==========================================
          BÖLÜM 5: ORDER BOOK CTA (TAM GENİŞLİK FOOTER GİBİ)
          ========================================== */}
      {book.orderUrl && (
        <section className="py-5 mt-auto w-100" style={{ backgroundColor: '#1a365d' }}>
          <Container className="text-center text-white py-5 my-lg-4">
            <h2 className="classy-heading text-white display-4 mb-4">Bring the Story Home</h2>
            <p className="lead mb-5 mx-auto" style={{ maxWidth: '700px', opacity: '0.9', lineHeight: '1.8' }}>
              Order your physical copy today and embark on this wonderful journey with your little ones.
            </p>
            <Button 
              href={book.orderUrl} 
              target="_blank" 
              variant="light" 
              size="lg" 
              className="px-5 py-3 rounded-pill fw-bold shadow-lg text-primary text-uppercase" 
              style={{ letterSpacing: '2px' }}
            >
              <i className="bi bi-cart-fill me-2 fs-5"></i> ORDER BOOK HERE
            </Button>
          </Container>
        </section>
      )}

    </div>
  );
};

export default BookLandingPage;